import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFortnights, type FortnightRead } from './use-fortnights';

/**
 * Which fortnight the app is looking at — the one thing every page agrees on.
 *
 * **The URL is the state.** `?fn=FN-14` on any route renders that fortnight,
 * which is what makes history shareable: a link pasted into the meeting chat
 * reopens exactly what the sender was looking at, and the back button walks
 * the fortnights someone stepped through. Holding the selection in a Context
 * or a store instead would make every one of those a bug report.
 *
 * With no `?fn=`, the server's `isCurrent` wins. That is deliberate: the
 * calendar's "which fortnight is now" rule (decision D2 — the latest whose
 * meeting date is not in the future) is computed once, server-side, and no
 * browser reimplements it.
 *
 * **Forward stops at today.** The calendar runs to FN-78, and every fortnight
 * past the current one is an empty shell with no data in it. Walking into one
 * would show a full page of blank panels that reads as a broken app rather
 * than as the future, so `next` is null at the current fortnight.
 */

export interface SelectedFortnight {
  /** `?fn=` if present, else the current fortnight; null until the calendar loads. */
  readonly fnId: string | null;
  readonly fortnight: FortnightRead | null;
  /** True when looking at the working copy rather than back at history. */
  readonly isCurrent: boolean;
  /** True when the fortnight is frozen — every figure came from its snapshot. */
  readonly isPublished: boolean;
  /** The fortnight one step back, or null at the start of the calendar. */
  readonly previous: FortnightRead | null;
  /** The fortnight one step forward, or null at the current fortnight. */
  readonly next: FortnightRead | null;
  readonly isPending: boolean;
  /** Navigate to a fortnight. `null` returns to the current one. */
  readonly select: (fnId: string | null) => void;
}

/** The search-param name. One constant so the reader and the writer agree. */
export const FN_PARAM = 'fn';

export function useSelectedFortnight(): SelectedFortnight {
  const [params, setParams] = useSearchParams();
  const query = useFortnights();
  const requested = params.get(FN_PARAM);

  const select = useCallback(
    (fnId: string | null) => {
      const next = new URLSearchParams(params);
      if (fnId === null) next.delete(FN_PARAM);
      else next.set(FN_PARAM, fnId);
      // `replace: false` — stepping through fortnights is navigation, and the
      // back button should undo it one step at a time.
      setParams(next);
    },
    [params, setParams]
  );

  return useMemo(() => {
    const rows = query.data?.rows ?? [];
    // The server pages newest-first; walking history is easier oldest-first,
    // and sorting here rather than trusting the order means a change to the
    // endpoint's default sort cannot silently reverse the arrows.
    const calendar = [...rows].sort((a, b) => a.ordinal - b.ordinal);
    const current = calendar.find((row) => row.isCurrent) ?? null;

    const selected =
      (requested === null ? null : (calendar.find((row) => row.fnId === requested) ?? null)) ??
      current;

    if (selected === null) {
      return {
        // Echo an unknown `?fn=` back rather than silently substituting the
        // current fortnight: the page endpoint 404s on it, and a 404 naming
        // the fortnight is far better than real-looking numbers for the wrong
        // one (the same reasoning as `resolve_or_404` on the backend).
        fnId: requested,
        fortnight: null,
        isCurrent: false,
        isPublished: false,
        previous: null,
        next: null,
        isPending: query.isPending,
        select,
      };
    }

    const index = calendar.indexOf(selected);
    const ceiling = current === null ? calendar.length - 1 : calendar.indexOf(current);

    return {
      fnId: selected.fnId,
      fortnight: selected,
      isCurrent: selected.isCurrent,
      isPublished: selected.status === 'published',
      previous: index > 0 ? (calendar[index - 1] ?? null) : null,
      next: index < ceiling ? (calendar[index + 1] ?? null) : null,
      isPending: query.isPending,
      select,
    };
  }, [query.data, query.isPending, requested, select]);
}
